import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Activity, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ClearableInput from "../components/ClearableInput";
import AdminSubNav from "../components/AdminSubNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogs = ({ user, setUser }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditSearch, setAuditSearch] = useState("");
  const [downloadSearch, setDownloadSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredAuditLogs = auditLogs.filter((log) => {
    if (!auditSearch.trim()) return true;
    const term = auditSearch.toLowerCase();
    return (
      (log.action || "").toLowerCase().includes(term) ||
      (log.entity || "").toLowerCase().includes(term) ||
      (log.actor_id || "").toLowerCase().includes(term)
    );
  });

  const filteredDownloadLogs = downloadLogs.filter((log) => {
    if (!downloadSearch.trim()) return true;
    const term = downloadSearch.toLowerCase();
    return (
      (log.item_id || "").toLowerCase().includes(term) ||
      (log.user_id || "").toLowerCase().includes(term) ||
      (log.ip || "").toLowerCase().includes(term)
    );
  });

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [audit, download] = await Promise.all([
        axios.get(`${API}/admin/audit-logs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/download-logs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAuditLogs(audit.data);
      setDownloadLogs(download.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Admin Navigation */}
      <nav className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold" style={{ fontFamily: 'Fraunces, serif' }}>Admin Dashboard</h1>
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/')} variant="ghost" className="text-white hover:text-white hover:bg-slate-800">
                Public Site
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-slate-800">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <AdminSubNav user={user} activeTab="logs" />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Fraunces, serif' }}>System Logs</h2>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <Tabs defaultValue="audit" className="w-full">
            <TabsList>
              <TabsTrigger value="audit" data-testid="audit-logs-tab">
                <Activity className="w-4 h-4 mr-2" />
                Audit Logs
              </TabsTrigger>
              <TabsTrigger value="download" data-testid="download-logs-tab">
                <Download className="w-4 h-4 mr-2" />
                Download Logs
              </TabsTrigger>
            </TabsList>

            <TabsContent value="audit" className="mt-6">
              <div className="flex justify-end mb-3">
                <div className="w-80">
                  <ClearableInput
                    type="text"
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    placeholder="Search audit logs (action, entity, actor)"
                    className="text-sm"
                  />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Action</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Entity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Actor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredAuditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50" data-testid={`audit-log-${log.id}`}>
                        <td className="px-6 py-4 text-sm text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {log.entity}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {log.actor_id ? log.actor_id.substring(0, 8) : 'System'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {auditLogs.length === 0 && (
                  <div className="text-center py-12 text-slate-600">No audit logs yet</div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="download" className="mt-6">
              <div className="flex justify-end mb-3">
                <div className="w-80">
                  <ClearableInput
                    type="text"
                    value={downloadSearch}
                    onChange={(e) => setDownloadSearch(e.target.value)}
                    placeholder="Search download logs (item, user, IP)"
                    className="text-sm"
                  />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Item ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">User ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">IP Address</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredDownloadLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50" data-testid={`download-log-${log.id}`}>
                        <td className="px-6 py-4 text-sm text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {log.item_id.substring(0, 8)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {log.user_id ? log.user_id.substring(0, 8) : 'Anonymous'}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {log.ip || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {downloadLogs.length === 0 && (
                  <div className="text-center py-12 text-slate-600">No download logs yet</div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default AdminLogs;